import React, { useMemo } from 'react';
import styles from './FormComponent.module.scss';
import {
  FeedbackFormElement, FeedbackFormElementMultiScale,
  FeedbackFormElementSelect,
  FeedbackFormElementText,
  FeedbackFormElementTypeEnum,
  SelectOption,
} from '../../../../../../../types';
import IconTrash from '../../../../../../../assets/icons/trash_circle.svg?react';
import FormText from './form-text/FormText';
import FormSelect from './form-select/FormSelect';
import Select from '../../../../../../../components/select/Select';
import { TextInput, TextInputValue } from '../../../../../../../components/text-input/TextInput';
import { useFeedbackForm } from '../useFeedbackFormEdit';
import Checkbox from '../../../../../../../components/checkbox/Checkbox';
import MultiQuestionScale from './multi-question-scale/MultiQuestionScale';

type Props = {
  element: FeedbackFormElement;
  index: number;
  showError: boolean;
  disableDontSendToTeacher: boolean;
};

const AnswerTypeOptions: SelectOption[] = [
  { label: 'Text', value: FeedbackFormElementTypeEnum.text },
  { label: 'Text area', value: FeedbackFormElementTypeEnum.textarea },
  { label: 'Multi-choice', value: FeedbackFormElementTypeEnum.checkbox },
  { label: 'Multi-choice (Assessable)', value: FeedbackFormElementTypeEnum.checkboxAssessable },
  { label: 'Single-choice', value: FeedbackFormElementTypeEnum.radio },
  { label: 'Single-choice (Assessable)', value: FeedbackFormElementTypeEnum.radioAssessable },
  { label: 'Multi-question Scale', value: FeedbackFormElementTypeEnum.multiScale },
  { label: 'Multi-question Scale (Assessable)', value: FeedbackFormElementTypeEnum.multiScaleAssessable },
];

function FormComponent(props: Props) {
  const { element, index, showError, disableDontSendToTeacher } = props;

  const { changeElementsValue, removeQuestion, clearOptions } = useFeedbackForm();

  const content = useMemo(() => {
    const type = element.type;

    switch (type) {
      case FeedbackFormElementTypeEnum.textarea:
      case FeedbackFormElementTypeEnum.text: {
        return (
          <FormText element={element as FeedbackFormElementText}/>
        );
      }
      case FeedbackFormElementTypeEnum.radio:
      case FeedbackFormElementTypeEnum.checkbox: {
        return (
          <FormSelect showError={showError} element={element as FeedbackFormElementSelect} index={index}/>
        );
      }
      case FeedbackFormElementTypeEnum.checkboxAssessable:
      case FeedbackFormElementTypeEnum.radioAssessable: {
        return (
          <FormSelect showError={showError} element={element as FeedbackFormElementSelect} index={index} isAssessable/>
        );
      }
      case FeedbackFormElementTypeEnum.multiScale: {
        return (
          <MultiQuestionScale showError={showError} element={element as FeedbackFormElementMultiScale} index={index}/>
        );
      }
      case FeedbackFormElementTypeEnum.multiScaleAssessable: {
        return (
          <MultiQuestionScale showError={showError} element={element as FeedbackFormElementMultiScale} index={index} isAssessable />
        );
      }
      default: {
        return null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element, element.id, element.type, showError]);

  const handleChangeValue = (v: TextInputValue) => {
    changeElementsValue(v, `${index}.value`);
  };

  const handleChangeType = (v: SelectOption) => {
    changeElementsValue(v.value, `${index}.type`);

    if (v.value === FeedbackFormElementTypeEnum.text || v.value === FeedbackFormElementTypeEnum.textarea) {
      clearOptions(index);
    }
  };

  const handleChangeCheckbox = (field: string) => (value: boolean) => {
    changeElementsValue(value, `${index}.${field}`);
  };

  const handleRemove = () => removeQuestion(index);

  return (
    <div className={styles.wrapper}>
      <div className={`${styles.row} ${styles.relative}`}>
        <TextInput textArea allowSpecialCharacters={false} hasError={showError && !element.value} subTitle={showError && !element.value ? 'Please fill in the field.' : ''} label={() => (<div className={styles.header}>
          <label className={styles.title}>Question {index + 1}</label>
          <div className={styles.actions} onClick={handleRemove}>
            <IconTrash className={styles.removeIcon}/>
          </div>
        </div>)} id={element.id} value={element.value} placeholder={element.placeholder} onChange={handleChangeValue}/>
      </div>
      <div className={styles.row}>
        <Select label={'Type'} options={AnswerTypeOptions} value={element.type} onChange={handleChangeType}/>
      </div>
      <div className={styles.content}>
        {content}
      </div>
      <div className={styles.row}>
        <Checkbox id={'doNotSendToTeacher'} disabled={disableDontSendToTeacher} checked={!!element.doNotSendToTeacher} onChange={handleChangeCheckbox('doNotSendToTeacher')} label={'Do not send answers to teacher'}/>
      </div>
      <div className={styles.row}>
        <Checkbox id={'isSensitive'} checked={!!element.isSensitive} onChange={handleChangeCheckbox('isSensitive')} label={'Sensitive data'}/>
      </div>
      <div className={styles.row}>
        <Checkbox id={'askExplanation'} checked={!!element.askExplanation} onChange={handleChangeCheckbox('askExplanation')} label={'Ask Explanation'}/>
      </div>
      <div className={styles.divider}/>
    </div>
  );
}

export default FormComponent;
